import { useSnapshot } from "valtio";
import { stateNavigation, statePlayerPositionS, stateWallPush, stateDevice, stateIframeWindow } from "../store";
import { ROOM_CONFIG } from "./roomConfig";
import { BsListUl } from "react-icons/bs";
import { RiWalkFill } from "react-icons/ri";
import { useState, useRef, useLayoutEffect } from "react";
import * as THREE from "three";
import { copyCamPosition, copyTargetPosition } from "../Utils/DebugCamCopy.jsx";

export default function Header({ waypointsVariable = [], onSelectWaypoint, activeIndex, isEnglisch }) {
  const snapStateNavigation = useSnapshot(stateNavigation);
  const snapStateDevice = useSnapshot(stateDevice);
  const snapIframeWindow = useSnapshot(stateIframeWindow);
  // Bei geöffnetem Modelviewer den Clicknavi-Header sperren (analog dazu, wie
  // die Galerie ihn per Overlay verdeckt): Buttons nicht klickbar + leichter
  // Blur/Abdunklung als sichtbares "inaktiv"-Signal. Nur Filter, kein opacity —
  // Filter wird von nichts überschrieben. Klicks/Drags gehen zum Canvas durch.
  const modelViewerActive = snapIframeWindow.LowModelOrbit;
  const isPortrait = snapStateDevice.isPortrait;
  // Portrait, or a narrow landscape phone — halve the edge/gap spacing in both cases.
  const isCompactHeader = isPortrait || snapStateDevice.isHandy;
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const salzzeitRef = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);

  // Justify the two small subtitle lines to exactly match SALZZEIT's rendered
  // width by stretching their letter-spacing — a manual measurement instead of
  // CSS text-justify, which iOS Safari doesn't support reliably.
  useLayoutEffect(() => {
    let cancelled = false;
    const justify = () => {
      const salzzeitEl = salzzeitRef.current;
      if (!salzzeitEl) return;
      // SALZZEIT is also a width:auto block sharing the parent with line2/3 —
      // if a subtitle line ever overshoots even slightly, the shared parent
      // grows and SALZZEIT gets stretched along with it, inflating targetWidth
      // on the next call and compounding the error. Pin it to max-content too
      // so it always reports its own true intrinsic width.
      salzzeitEl.style.width = "max-content";
      const targetWidth = salzzeitEl.offsetWidth;
      if (!targetWidth) return;
      [line2Ref, line3Ref].forEach((ref) => {
        const el = ref.current;
        if (!el) return;
        // Block-level els stretch to the parent's width (set by SALZZEIT, the
        // widest sibling) — measure intrinsic text width via max-content instead
        // of offsetWidth, or naturalWidth would just read back that same stretched width.
        el.style.width = "max-content";
        el.style.letterSpacing = "0px";
        const naturalWidth = el.offsetWidth;
        // CSS letter-spacing adds space after every character, including the
        // last one — so divide by the full character count, not count-1, or
        // the trailing spacing pushes the line past targetWidth.
        const gaps = Math.max((el.textContent || "").length, 1);
        let extra = Math.max((targetWidth - naturalWidth) / gaps, 0);
        el.style.letterSpacing = `${extra}px`;
        // Verify against the actual rendered result — rounding/kerning can
        // still push it past targetWidth, so nudge down until it genuinely
        // fits instead of trusting the arithmetic alone.
        let guard = 0;
        while (el.offsetWidth > targetWidth && extra > 0 && guard < 30) {
          extra = Math.max(extra - 0.2, 0);
          el.style.letterSpacing = `${extra}px`;
          guard++;
        }
      });
    };

    justify();
    // Re-measure repeatedly for a bit — on a slower real device the stylesheet
    // (Tailwind classes) or web font can still be settling after mount, and a
    // single next-frame check isn't always enough to catch the final layout.
    document.fonts?.ready?.then(() => { if (!cancelled) justify(); });
    const raf = requestAnimationFrame(justify);
    const timeouts = [50, 150, 300, 600, 1000].map((ms) =>
      setTimeout(() => { if (!cancelled) justify(); }, ms)
    );
    window.addEventListener("resize", justify);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      timeouts.forEach(clearTimeout);
      window.removeEventListener("resize", justify);
    };
  }, [isCompactHeader]);

  return (
    <header className={`absolute top-0 left-0 w-full flex justify-between items-center z-50 select-none transition-[filter] duration-300 ${modelViewerActive ? "blur-[3px] brightness-[0.55]" : ""} ${isCompactHeader ? "pl-2 pr-1 py-2" : "px-8 py-4"}`}>

      {/* --- Logo --- */}
      <div className={`flex items-center ${modelViewerActive ? "pointer-events-none" : "pointer-events-auto"} ${isCompactHeader ? "space-x-2" : "space-x-4"}`}>
        <img
          src="../UI/IconMsueumHeader.svg"
          alt="Museum Icon"
          className={`opacity-80 ${isCompactHeader ? "w-10" : "w-14"}`}
          draggable="false"
        />
        <div>
          <div ref={salzzeitRef} className={`text-white font-basic tracking-[0.35rem] leading-tight whitespace-nowrap ${isCompactHeader ? "text-2xl" : "text-4xl"}`}>
            SALZZEIT
          </div>
          <div ref={line2Ref} className="text-gray-300 text-[7.3px] uppercase leading-tight whitespace-nowrap">
            Salzkammergut
          </div>
          <div ref={line3Ref} className="text-gray-300 text-[7.3px] uppercase leading-tight whitespace-nowrap">
            Connected History
          </div>
        </div>
      </div>

      {/* --- Buttons --- */}
      <div className={`flex items-center ${modelViewerActive ? "pointer-events-none" : "pointer-events-auto"} ${isCompactHeader ? "space-x-2" : "space-x-4"}`}>

        {/* Waypoint-Authoring-Helfer — bei Bedarf wieder einkommentieren
            (DebugCamTracker in Experience.jsx liefert die Werte). */}
        {/* <button
          className="rounded-full border-2 border-orange-400 bg-black/30 hover:bg-black/50 transition-all cursor-pointer text-orange-300 font-mono px-3 py-1 text-[10px]"
          onClick={copyCamPosition}
        >
          copy cam
        </button>
        <button
          className="rounded-full border-2 border-orange-400 bg-black/30 hover:bg-black/50 transition-all cursor-pointer text-orange-300 font-mono px-3 py-1 text-[10px]"
          onClick={copyTargetPosition}
        >
          copy target
        </button> */}

        {/* List button + dropdown */}
        <div className="relative">
          <div
            className={`flex items-center justify-center rounded-full border-2 cursor-pointer transition-all
              ${isCompactHeader ? "w-11 h-11" : "w-14 h-14"}
              ${snapStateNavigation.waypointListOpen
                ? "border-orange-400 bg-black/50"
                : "border-white/30 bg-black/30 hover:border-white/50 hover:bg-black/50"
              }`}
            onClick={() => {
              // Hover-Reset: auf Touch feuert mouseenter beim Tippen, mouseleave
              // aber nie — ohne Reset bliebe der zuletzt getippte Dot orange.
              setHoveredIndex(null);
              stateNavigation.waypointListOpen = !stateNavigation.waypointListOpen;
            }}
          >
            <BsListUl color="white" size={isCompactHeader ? 16 : 22} />
          </div>

          {snapStateNavigation.waypointListOpen && (
            <div
              style={{
                position: isPortrait && snapStateDevice.isHandy ? "fixed" : "absolute",
                top: isPortrait && snapStateDevice.isHandy ? "calc(env(safe-area-inset-top, 0px) + 70px)" : "calc(100% + 10px)",
                // Mobile Portrait: horizontal auf dem Screen zentrieren statt
                // rechtsbündig am Listen-Icon hängend (das Icon sitzt weit
                // rechts im Header, die Liste würde sonst schief wirken).
                ...(isPortrait && snapStateDevice.isHandy
                  ? { left: "50%", transform: "translateX(-50%)" }
                  : { right: 0 }),
                minWidth: isCompactHeader ? "160px" : "220px",
                maxHeight: isCompactHeader ? "55vh" : "70vh",
                overflowY: "auto",
                background: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(6px)",
                borderRadius: "10px",
                padding: isCompactHeader ? "6px 0" : "10px 0",
                zIndex: 50,
                // Mobile Portrait: lange Listen in zwei Spalten aufteilen
                // (multi-column füllt spaltenweise — erste Hälfte links).
                ...(isPortrait && snapStateDevice.isHandy
                  ? { columnCount: 2, columnGap: "0px", minWidth: "300px", maxWidth: "92vw" }
                  : {}),
              }}
            >
              {waypointsVariable.map((wp, i) => {
                const label = isEnglisch && wp.TitelEn ? wp.TitelEn : wp.Titel;
                if (!label || !label.trim()) return null;
                const isCurrent = i === activeIndex;
                const isHovered = i === hoveredIndex;
                const isHighlit = isCurrent || isHovered;

                return (
                  <button
                    key={wp.Id}
                    onClick={() => {
                      setHoveredIndex(null);
                      if (onSelectWaypoint) onSelectWaypoint(wp, i);
                    }}
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: isCompactHeader ? "8px" : "12px",
                      width: "100%",
                      padding: isCompactHeader ? "6px 12px" : "9px 18px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "'Helvetica Neue', Arial, sans-serif",
                      // Verhindert, dass ein Button beim Zwei-Spalten-Layout
                      // (Mobile Portrait) über den Spaltenumbruch zerrissen wird.
                      breakInside: "avoid",
                    }}
                  >
                    {/* Dot */}
                    <div style={{
                      flexShrink: 0,
                      width: isHighlit ? (isCompactHeader ? "10px" : "14px") : (isCompactHeader ? "8px" : "11px"),
                      height: isHighlit ? (isCompactHeader ? "10px" : "14px") : (isCompactHeader ? "8px" : "11px"),
                      borderRadius: "50%",
                      background: isHighlit ? "#f97316" : "white",
                      boxShadow: isHighlit ? "0 0 0 5px rgba(249,115,22,0.18)" : "none",
                      transition: "all 0.25s ease",
                    }} />
                    {/* Label */}
                    <span style={{
                      color: isCurrent ? "#f97316" : isHovered ? "#fff" : "rgba(255,255,255,0.75)",
                      fontSize: isCompactHeader ? "11px" : "13px",
                      fontWeight: isCurrent ? 700 : 400,
                      letterSpacing: isCurrent ? "1.2px" : "0.2px",
                      textTransform: isCurrent ? "uppercase" : "none",
                      transition: "all 0.2s ease",
                    }}>
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* FP button */}
        <div
          className={`flex items-center justify-center rounded-full border-2 border-white/30
            bg-black/30 hover:bg-black/50 transition-all cursor-pointer
            ${isCompactHeader ? "w-11 h-11" : "w-14 h-14"}`}
          onClick={() => {
            // Player an der ROHEN Waypoint-Position spawnen, nicht an wayCamPos:
            // auf Mobile schiebt applyOrientationOffset die Kamera um
            // CamPortrait/CamLandscape (bis ~40 Einheiten) vom Ziel weg — der
            // Player würde damit außerhalb kleiner CheckInArea-Sensoren landen
            // (z.B. Eisenzeit-Lebensbild), deren Annotationen im 3D-Player
            // positionsgesteuert sind, und die Texte verschwänden.
            const rawPos = waypointsVariable[activeIndex]?.Cam?.Position;
            const pos = rawPos
              ? { x: rawPos[0], y: rawPos[1], z: rawPos[2] }
              : stateNavigation.wayCamPos;
            statePlayerPositionS.playerPositionS = new THREE.Vector3(pos.x, pos.y, pos.z);
            statePlayerPositionS.playerInput = true;
            const ROOM_INDEX = {
              ThemeM_MuseenDerRegion: 0, ThemeM_Holzarbeit: 1, ThemeM_Klima: 2,
              ThemeM_Salzabbau: 3, ThemeM_Handelswege: 4, ThemeM_Menschen: 5,
            };
            let roomId = stateNavigation.waypointSetId;
            while (roomId && ROOM_INDEX[roomId] === undefined) {
              roomId = ROOM_CONFIG[roomId]?.parentId;
            }
            const idx = ROOM_INDEX[roomId];
            if (idx !== undefined) stateWallPush.activeThemeIndex = idx;
            stateNavigation.isClickNavi = true;
          }}
        >
          <RiWalkFill color="white" size={isCompactHeader ? 18 : 26} />
        </div>
      </div>
    </header>
  );
}
